import { useCallback, useMemo, useState } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { FASES_PEDIDO } from 'components/Trabajador/fases';

const PREF_KEY_PREFIX = 'agorax_kanban_prefs_';

// Estructura persistida: array de { key, label } en el orden elegido por el
// usuario. El color SIEMPRE se deriva de FASES_PEDIDO y no se guarda.
const DEFAULT_COLUMNAS = () =>
  FASES_PEDIDO.map((f) => ({ key: f.key, label: f.label }));

function readSaved(slug) {
  try {
    const raw = localStorage.getItem(slug);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed
      .filter((c) => c && typeof c.key === 'string' && typeof c.label === 'string')
      .map((c) => ({ key: c.key, label: c.label }));
  } catch (e) {
    return null;
  }
}

// Combina el orden/labels guardados con las fases reales:
// - conserva el orden guardado;
// - inserta al final las fases nuevas que no esten guardadas;
// - descarta fases guardadas que ya no existen.
function mergeColumnas(saved) {
  const porKey = new Map();
  saved.forEach((c) => porKey.set(c.key, c.label));

  const columnas = [];
  saved.forEach((c) => {
    if (FASES_PEDIDO.some((f) => f.key === c.key)) {
      columnas.push(c);
    }
  });
  FASES_PEDIDO.forEach((f) => {
    if (!porKey.has(f.key)) {
      columnas.push({ key: f.key, label: f.label });
    }
  });
  return columnas;
}

/**
 * Personalizacion del kanban de pedidos (orden + nombre visual de columnas)
 * persistida en localStorage por usuario.
 *
 * columnas: array ordenado { key, label, color }.
 * isCustom: true si el usuario altero orden o nombres.
 */
export default function useKanbanPrefs() {
  const { usuario } = useAuth();
  const cedula = usuario?.cedula || usuario?.usuario || 'global';
  const storageKey = PREF_KEY_PREFIX + cedula;

  const [columnas, setColumnas] = useState(() => {
    const saved = readSaved(storageKey);
    return saved && saved.length > 0 ? mergeColumnas(saved) : DEFAULT_COLUMNAS();
  });

  const persist = useCallback(
    (next) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch (e) {
        /* storage lleno o no disponible */
      }
    },
    [storageKey]
  );

  const rename = useCallback(
    (key, label) => {
      const next = columnas.map((c) =>
        c.key === key ? { ...c, label: String(label || '').trim() || c.label } : c
      );
      setColumnas(next);
      persist(next);
    },
    [columnas, persist]
  );

  const move = useCallback(
    (from, to) => {
      if (from === to || from < 0 || to < 0) return;
      if (from >= columnas.length || to >= columnas.length) return;
      const next = columnas.slice();
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      setColumnas(next);
      persist(next);
    },
    [columnas, persist]
  );

  const reset = useCallback(() => {
    const def = DEFAULT_COLUMNAS();
    setColumnas(def);
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      /* noop */
    }
  }, [storageKey]);

  const isCustom = useMemo(() => {
    if (columnas.length !== FASES_PEDIDO.length) return true;
    return columnas.some((c, i) => {
      const def = FASES_PEDIDO[i];
      return !def || c.key !== def.key || c.label !== def.label;
    });
  }, [columnas]);

  const columnasConColor = useMemo(
    () =>
      columnas.map((c) => ({
        ...c,
        color: (FASES_PEDIDO.find((f) => f.key === c.key) || { color: '#1a73e8' }).color,
      })),
    [columnas]
  );

  return { columnas: columnasConColor, rename, move, reset, isCustom };
}
